import { getValidAccessToken } from './getValidAccessToken';
import { normalizeEnvKey } from './envKeyUtils';
import { decodeJwtParts } from './debug/jwtDebug';

const getFunctionsUrl = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) throw new Error('Missing REACT_APP_SUPABASE_URL');
  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/daily-admin`;
};

const resolveApiKey = () => {
  const jwtAnonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_JWT_ANON_KEY);
  const anonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_ANON_KEY);

  const jwtAnonDecoded = decodeJwtParts(jwtAnonRaw);
  const jwtAnonIsUsableJwt =
    jwtAnonDecoded?.partsCount === 3 &&
    !jwtAnonDecoded?.decodeError &&
    jwtAnonDecoded?.payload;

  const anonDecoded = decodeJwtParts(anonRaw);
  const anonLooksJwt =
    anonDecoded?.partsCount === 3 &&
    !anonDecoded?.decodeError &&
    anonDecoded?.payload;

  if (jwtAnonIsUsableJwt) return jwtAnonRaw;
  if (anonLooksJwt) return anonRaw;
  return null;
};

const buildAuthHeaders = async ({ includeContentType = true } = {}) => {
  const headers = {};
  if (includeContentType) headers['Content-Type'] = 'application/json';

  const accessToken = await getValidAccessToken();
  if (!accessToken) {
    const error = new Error('Missing auth session.');
    error.code = 'UNAUTHORIZED';
    error.status = 401;
    throw error;
  }
  headers.Authorization = `Bearer ${accessToken}`;

  const apiKey = resolveApiKey();
  if (apiKey) headers.apikey = apiKey;

  return headers;
};

const parseJsonResponse = async (response) => {
  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  return { data, text };
};

const requestWithRetry = async ({ body, asFormData = false }) => {
  const initialHeaders = await buildAuthHeaders({ includeContentType: !asFormData });

  const doRequest = (headers) =>
    fetch(getFunctionsUrl(), {
      method: 'POST',
      headers,
      body: asFormData ? body : JSON.stringify(body),
    });

  let response = await doRequest(initialHeaders);
  if (response.status === 401) {
    const refreshed = await getValidAccessToken({ forceRefresh: true });
    if (refreshed) {
      const retryHeaders = { ...initialHeaders, Authorization: `Bearer ${refreshed}` };
      response = await doRequest(retryHeaders);
    }
  }

  const { data, text } = await parseJsonResponse(response);
  if (!response.ok) {
    const error = new Error(data?.error || text || 'Daily admin request failed.');
    error.code = data?.code;
    error.status = response.status;
    throw error;
  }

  return data || {};
};

export const listDailyPuzzlesAdmin = async () =>
  await requestWithRetry({
    body: { action: 'list' },
  });

export const ensureDailyBufferAdmin = async () =>
  await requestWithRetry({
    body: { action: 'ensure_buffer' },
  });

export const setManualDailyPuzzleAdmin = async ({
  puzzleDate,
  word,
  imageUrl,
  zoomOriginX = 0.5,
  zoomOriginY = 0.5,
  zoomRadius = 0.25,
}) =>
  await requestWithRetry({
    body: {
      action: 'set_manual_puzzle',
      puzzle_date: puzzleDate,
      word,
      image_url: imageUrl,
      zoom_origin_x: zoomOriginX,
      zoom_origin_y: zoomOriginY,
      zoom_radius: zoomRadius,
    },
  });

export const uploadDailyCustomImageAdmin = async ({ file, puzzleDate }) => {
  const formData = new FormData();
  formData.append('action', 'upload_custom_image');
  formData.append('puzzle_date', puzzleDate);
  formData.append('file', file);

  return await requestWithRetry({
    body: formData,
    asFormData: true,
  });
};

export const deleteManualDailyPuzzleAdmin = async (puzzleDate) =>
  await requestWithRetry({
    body: {
      action: 'delete_manual_puzzle',
      puzzle_date: puzzleDate,
    },
  });

export const regenerateDailyRiddleAdmin = async (puzzleDate) =>
  await requestWithRetry({
    body: {
      action: 'regenerate_riddle',
      puzzle_date: puzzleDate,
    },
  });

export const regenerateDailyImageAdmin = async (puzzleDate) =>
  await requestWithRetry({
    body: {
      action: 'regenerate_image',
      puzzle_date: puzzleDate,
    },
  });

export const updateDailyZoomOriginAdmin = async ({
  puzzleDate,
  zoomOriginX,
  zoomOriginY,
}) =>
  await requestWithRetry({
    body: {
      action: 'update_zoom_origin',
      puzzle_date: puzzleDate,
      zoom_origin_x: zoomOriginX,
      zoom_origin_y: zoomOriginY,
    },
  });

